import React from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";

export type MultiSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

/** When set (multi-select only), options render under LOF-style headings with a group checkbox. */
export type MultiSelectOptionGroup = {
  groupKey: string;
  heading: string;
  options: MultiSelectOption[];
};

type MultiSelectDropdownProps = {
  label: string;
  options: MultiSelectOption[];
  /** Grouped list UI; must cover the same option values as `options`. */
  optionGroups?: MultiSelectOptionGroup[];
  /** When true: checkbox multi-select. When false: single-select. */
  multiple?: boolean;
  /**
   * If true, empty value means "All" (filter semantics).
   * - For `multiple`: `value=[]` means All.
   * - For `multiple=false`: `value=null` means no selection (placeholder).
   */
  emptyMeansAll?: boolean;
  /** Summary shown when empty selection + emptyMeansAll. Default: "ทั้งหมด" */
  allLabel?: string;
  /** Placeholder shown when nothing selected in single mode. */
  placeholder?: string;
  /** Show a search input above options. Default: true */
  searchable?: boolean;
  /**
   * Show select-all action (every option value). Default: true in multiple mode.
   * Hidden when `emptyMeansAll` — clearing to `[]` already means “all”; avoids passing a large ID array.
   */
  showSelectAll?: boolean;
  /** Show clear action. In filter mode this means "clear to all". Default: true */
  showClear?: boolean;
  /** Optional id on the trigger button (e.g. for tests or label association). */
  triggerButtonId?: string;
  className?: string;
} & (
  | {
      multiple: true;
      value: string[];
      onChange: (next: string[]) => void;
    }
  | {
      multiple?: false;
      value: string | null;
      onChange: (next: string | null) => void;
    }
);

const normalize = (s: string) => s.trim().toLowerCase();

function GroupTriCheckbox({
  id,
  checked,
  indeterminate,
  onChange,
}: {
  id: string;
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="mt-1 size-6 shrink-0 rounded border border-slate-300 text-indigo-600 focus:ring-indigo-500/40"
      aria-label={indeterminate ? "บางส่วนในกลุ่ม" : checked ? "เลือกกลุ่มทั้งหมด" : "ไม่เลือกกลุ่ม"}
    />
  );
}

export function MultiSelectDropdown(props: MultiSelectDropdownProps) {
  const {
    label,
    options,
    searchable = true,
    className,
    placeholder = "เลือก...",
    allLabel = "ทั้งหมด",
    emptyMeansAll = false,
    showClear = true,
    triggerButtonId,
  } = props;

  const multiple = props.multiple === true;
  const showSelectAll = props.showSelectAll ?? multiple;
  const showSelectAllButton = Boolean(multiple && !emptyMeansAll && showSelectAll);

  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [collapsedGroupKeys, setCollapsedGroupKeys] = React.useState<Set<string>>(() => new Set());

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    if (!searchable) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, searchable]);

  const enabledValues = React.useMemo(
    () => options.filter((o) => !o.disabled).map((o) => o.value),
    [options],
  );

  const selectedValues = React.useMemo(() => {
    if (!multiple) return [];
    const raw = props.value ?? [];
    if (emptyMeansAll && raw.length === 0) return enabledValues;
    return raw;
  }, [multiple, props, emptyMeansAll, enabledValues]);

  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  const filteredOptions = React.useMemo(() => {
    const q = normalize(query);
    if (!q) return options;
    return options.filter((o) => normalize(o.label).includes(q) || normalize(o.value).includes(q));
  }, [options, query]);

  const optionGroups = props.optionGroups;

  const filteredGroups = React.useMemo(() => {
    if (!multiple || !optionGroups?.length) return null;
    const q = normalize(query);
    return optionGroups
      .map((g) => ({
        ...g,
        options: q
          ? g.options.filter(
              (o) =>
                normalize(o.label).includes(q) ||
                normalize(o.value).includes(q) ||
                normalize(g.heading).includes(q),
            )
          : g.options,
      }))
      .filter((g) => g.options.length > 0);
  }, [optionGroups, query, multiple]);

  const summary = React.useMemo(() => {
    if (multiple) {
      const raw = props.value;
      if (emptyMeansAll && raw.length === 0) return allLabel;
      if (selectedValues.length === 0) return "ไม่เลือก";
      if (selectedValues.length === 1) {
        const one = options.find((o) => o.value === selectedValues[0]);
        return one?.label ?? selectedValues[0];
      }
      return `เลือก ${selectedValues.length} รายการ`;
    }

    const v = (props as Extract<MultiSelectDropdownProps, { multiple?: false }>).value;
    if (!v) return placeholder;
    return options.find((o) => o.value === v)?.label ?? v;
  }, [multiple, props, emptyMeansAll, allLabel, selectedValues, options, placeholder]);

  const handleToggle = React.useCallback(
    (value: string) => {
      if (!multiple) {
        const cur = (props as Extract<MultiSelectDropdownProps, { multiple?: false }>).value;
        (props as Extract<MultiSelectDropdownProps, { multiple?: false }>).onChange(cur === value ? null : value);
        setOpen(false);
        return;
      }

      const current = selectedValues;
      const isSelected = current.includes(value);
      const next = isSelected ? current.filter((v) => v !== value) : [...current, value];
      (props as Extract<MultiSelectDropdownProps, { multiple: true }>).onChange(next);
    },
    [multiple, props, selectedValues],
  );

  const handleSelectAll = React.useCallback(() => {
    if (!multiple) return;
    (props as Extract<MultiSelectDropdownProps, { multiple: true }>).onChange(enabledValues);
  }, [multiple, props, enabledValues]);

  const handleClear = React.useCallback(() => {
    if (multiple) {
      (props as Extract<MultiSelectDropdownProps, { multiple: true }>).onChange([]);
      return;
    }
    (props as Extract<MultiSelectDropdownProps, { multiple?: false }>).onChange(null);
  }, [multiple, props]);

  const handleGroupToggle = React.useCallback(
    (group: MultiSelectOptionGroup) => {
      if (!multiple) return;
      const groupValues = group.options.filter((o) => !o.disabled).map((o) => o.value);
      if (groupValues.length === 0) return;

      const raw = (props as Extract<MultiSelectDropdownProps, { multiple: true }>).value ?? [];
      const baseSelection = emptyMeansAll && raw.length === 0 ? [...enabledValues] : [...raw];
      const allInGroup = groupValues.every((v) => baseSelection.includes(v));
      const next = allInGroup
        ? baseSelection.filter((v) => !groupValues.includes(v))
        : Array.from(new Set([...baseSelection, ...groupValues]));
      (props as Extract<MultiSelectDropdownProps, { multiple: true }>).onChange(next);
    },
    [emptyMeansAll, enabledValues, multiple, props],
  );

  const handleSelectOnlyGroup = React.useCallback(
    (group: MultiSelectOptionGroup) => {
      if (!multiple) return;
      const groupValues = group.options.filter((o) => !o.disabled).map((o) => o.value);
      if (groupValues.length === 0) return;
      (props as Extract<MultiSelectDropdownProps, { multiple: true }>).onChange(groupValues);
    },
    [multiple, props],
  );

  const handleToggleGroupExpanded = React.useCallback((groupKey: string) => {
    setCollapsedGroupKeys((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const ariaLabel = multiple ? `${label} — ปัจจุบัน: ${summary}` : `${label} — ปัจจุบัน: ${summary}`;

  const useGroupedList = Boolean(multiple && optionGroups?.length);
  const isSearching = normalize(query).length > 0;

  const rawMultiValue = multiple
    ? ((props as Extract<MultiSelectDropdownProps, { multiple: true }>).value ?? [])
    : [];
  const resetToAllDisabled = Boolean(multiple && emptyMeansAll && rawMultiValue.length === 0);

  return (
    <div ref={wrapRef} className={cn("relative flex min-h-9 flex-col text-base", className)}>
      <span className="text-slate-700">{label}</span>
      <button
        id={triggerButtonId}
        type="button"
        className="mt-1 inline-flex min-h-9 w-full min-w-[200px] max-w-xs items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-base text-slate-800 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 sm:max-w-sm"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="min-w-0 flex-1 truncate font-medium">{summary}</span>
        <ChevronDown
          className={cn("h-5 w-5 shrink-0 text-slate-500 transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-50 mt-1 w-[min(100vw-2rem,28rem)] max-w-md overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {searchable ? (
            <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
              <Search className="h-4 w-4 text-slate-400" aria-hidden />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ค้นหา..."
                className="min-h-9 flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="inline-flex min-h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-2 text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                  aria-label="ล้างคำค้นหา"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
          ) : null}

          {(showSelectAllButton || showClear) && (
            <div className="flex flex-wrap gap-2 border-b border-slate-100 px-3 py-2">
              {showSelectAllButton ? (
                <button
                  type="button"
                  className="min-h-9 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-800 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                  onClick={handleSelectAll}
                >
                  เลือกทั้งหมด
                </button>
              ) : null}
              {showClear ? (
                <button
                  type="button"
                  disabled={resetToAllDisabled}
                  title={
                    resetToAllDisabled
                      ? 'อยู่ในโหมดแสดงทั้งหมดอยู่แล้ว'
                      : multiple && emptyMeansAll
                        ? 'ล้างการเลือกเพื่อแสดงข้อมูลทุกหน่วยงาน'
                        : undefined
                  }
                  className={cn(
                    'min-h-9 px-3 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                    multiple && emptyMeansAll && !resetToAllDisabled
                      ? [
                          'rounded-[10px] border-2 border-black bg-[#71EB4C] text-base font-bold text-black',
                          'shadow-[5px_5px_0_#000] transition-all duration-300',
                          'hover:scale-105 hover:border-[#EB4CC1] hover:bg-white hover:text-[#EB4CC1] hover:shadow-[5px_5px_0_#EB4CC1]',
                          'active:translate-y-1 active:scale-100 active:border-black active:bg-[#fcf414] active:text-black active:shadow-none',
                          'focus-visible:ring-[#71EB4C]',
                        ]
                      : resetToAllDisabled
                        ? 'cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 text-slate-400 focus-visible:ring-indigo-500/40'
                        : 'rounded-md border border-slate-200 bg-white text-indigo-800 hover:bg-slate-50 focus-visible:ring-indigo-500/40',
                  )}
                  onClick={handleClear}
                >
                  {multiple && emptyMeansAll ? 'กลับเป็นทั้งหมด' : 'ล้าง'}
                </button>
              ) : null}
            </div>
          )}

          <div className={cn("overflow-y-auto p-3", useGroupedList ? "max-h-96" : "max-h-72")}>
            {useGroupedList ? (
              !filteredGroups?.length ? (
                <div className="py-6 text-center text-sm text-slate-500">ไม่พบรายการ</div>
              ) : (
                <div className="space-y-4">
                  {filteredGroups.map((group) => {
                    const groupVals = group.options.filter((o) => !o.disabled).map((o) => o.value);
                    const selectedInGroup = groupVals.filter((v) => selectedSet.has(v)).length;
                    const allIn = groupVals.length > 0 && selectedInGroup === groupVals.length;
                    const someIn = selectedInGroup > 0 && !allIn;
                    const groupCbId = `msd-grp-${label}-${group.groupKey}`.replace(/[^a-zA-Z0-9_-]/g, "_");
                    const groupPanelId = `${groupCbId}-panel`;
                    const expanded = isSearching || !collapsedGroupKeys.has(group.groupKey);
                    return (
                      <div key={group.groupKey} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                        <div className="flex items-start gap-2">
                          <GroupTriCheckbox
                            id={groupCbId}
                            checked={allIn}
                            indeterminate={someIn}
                            onChange={() => handleGroupToggle(group)}
                          />
                          <label
                            htmlFor={groupCbId}
                            className="cursor-pointer py-0.5 text-sm font-semibold leading-snug text-slate-900"
                          >
                            {group.heading}
                          </label>
                          <span className="mt-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            {group.options.length}
                          </span>
                          <button
                            type="button"
                            className="ml-auto min-h-8 rounded-md border border-indigo-100 bg-indigo-50 px-2.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                            aria-label={`เลือกเฉพาะ ${group.heading}`}
                            onClick={() => handleSelectOnlyGroup(group)}
                          >
                            เลือกเฉพาะ
                          </button>
                          <button
                            type="button"
                            className="min-h-8 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                            aria-controls={groupPanelId}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? "ย่อ" : "ขยาย"} ${group.heading}`}
                            onClick={() => handleToggleGroupExpanded(group.groupKey)}
                          >
                            {expanded ? "ย่อ" : "ขยาย"}
                          </button>
                        </div>
                        {expanded ? (
                          <ul id={groupPanelId} className="mt-2 space-y-2 border-l-2 border-slate-100 pl-3 sm:pl-4">
                            {group.options.map((opt) => {
                              const checked = selectedSet.has(opt.value);
                              const id = `msd-${label}-${opt.value}`.replace(/[^a-zA-Z0-9_-]/g, "_");
                              return (
                                <li key={opt.value} className={cn(opt.disabled && "opacity-50")}>
                                  <div className="flex items-start gap-2">
                                    <input
                                      id={id}
                                      type="checkbox"
                                      checked={checked}
                                      disabled={opt.disabled}
                                      onChange={() => handleToggle(opt.value)}
                                      className="mt-1 size-6 shrink-0 rounded border border-slate-300 text-indigo-600 focus:ring-indigo-500/40"
                                    />
                                    <label
                                      htmlFor={id}
                                      className="min-h-6 cursor-pointer py-1 text-sm leading-snug text-slate-800"
                                    >
                                      {opt.label}
                                    </label>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )
            ) : filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">ไม่พบรายการ</div>
            ) : (
              <ul className="space-y-2.5">
                {filteredOptions.map((opt) => {
                  const checked = multiple
                    ? selectedSet.has(opt.value)
                    : (props as Extract<MultiSelectDropdownProps, { multiple?: false }>).value === opt.value;
                  const id = `msd-${label}-${opt.value}`.replace(/[^a-zA-Z0-9_-]/g, "_");
                  return (
                    <li key={opt.value} className={cn(opt.disabled && "opacity-50")}>
                      <div className="flex items-start gap-2">
                        {multiple ? (
                          <input
                            id={id}
                            type="checkbox"
                            checked={checked}
                            disabled={opt.disabled}
                            onChange={() => handleToggle(opt.value)}
                            className="mt-1 size-6 shrink-0 rounded border border-slate-300 text-indigo-600 focus:ring-indigo-500/40"
                          />
                        ) : (
                          <button
                            type="button"
                            disabled={opt.disabled}
                            onClick={() => handleToggle(opt.value)}
                            className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded border border-slate-300 bg-white text-indigo-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 disabled:opacity-50"
                            aria-label={checked ? "เลือกแล้ว" : "ยังไม่เลือก"}
                          >
                            {checked ? <Check className="h-4 w-4" aria-hidden /> : null}
                          </button>
                        )}
                        <label
                          htmlFor={multiple ? id : undefined}
                          className={cn(
                            "min-h-6 cursor-pointer py-1 text-base leading-snug text-slate-800",
                            !multiple && "cursor-default",
                          )}
                        >
                          {opt.label}
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

